import { Building06Icon, Key01Icon, Logout03Icon, UnfoldMoreIcon } from '@hugeicons/core-free-icons';
import { ROLE_LABELS, type Home, type MeResponse, type Role } from '@lia/contracts';
import {
  AppShell,
  Avatar,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  ErrorState,
  Icon,
  ModeBanner,
  ModuleRail,
  ModuleRailItem,
  NavGroup,
  NavItem,
  NotificationButton,
  ProjectPicker,
  SearchInput,
  WorkspaceSwitcher,
  formatRelative,
} from '@lia/ui';
import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { Link, Outlet, useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { SoftphoneWidget } from '../components/softphone-widget.js';
import { api, ApiError } from '../lib/api.js';
import { useApiQuery } from '../lib/hooks.js';
import { wk } from '../lib/query.js';
import { SoftphoneProvider } from '../lib/softphone/softphone-context.js';
import { activeNavKey, moduleNav, navHref, RAIL_MODULES, railHref, railKeyFromPath, visibleRail, type ModuleEnablement, type RailKey } from './nav.js';
import { FullPageLoading } from './require-session.js';
import { makeCan, rememberWorkspace, useMe, useProjectFilter, WorkspaceProvider, type WorkspaceContextValue } from './session.js';

function Brand() {
  return (
    <Link to="/" className="flex items-center gap-2 rounded-control focus-visible:outline-2 focus-visible:outline-focus">
      <img src="/favicon.svg" alt="" className="size-7" />
      <span className="grid leading-tight">
        <span className="text-body-lg font-semibold text-text-primary">LIA</span>
        <span className="text-meta text-text-secondary">by Estateflow</span>
      </span>
    </Link>
  );
}

function useSignOut() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return React.useCallback(async () => {
    try {
      await api('logout');
    } catch {
      // The session may already be gone; sign-in is the right place either way.
    }
    qc.clear();
    navigate('/ingresar', { replace: true });
  }, [qc, navigate]);
}

function UserMenu({ me, workspaceId }: { me: MeResponse; workspaceId: string | null }) {
  const signOut = useSignOut();
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-3 rounded-control px-2 py-2 text-left hover:bg-surface-subtle focus-visible:outline-2 focus-visible:outline-focus"
          aria-label={`Cuenta de ${me.user.name}`}
        >
          <Avatar name={me.user.name || me.user.email} size="sm" />
          <span className="grid min-w-0 flex-1">
            <span className="truncate text-body font-medium text-text-primary">{me.user.name || 'Sin nombre'}</span>
            <span className="truncate text-meta text-text-secondary">{me.user.email}</span>
          </span>
          <Icon icon={UnfoldMoreIcon} className="size-4 text-text-secondary" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="top">
        <DropdownMenuLabel>{me.user.email}</DropdownMenuLabel>
        <DropdownMenuItem
          icon={<Icon icon={Key01Icon} />}
          onSelect={() => navigate(workspaceId ? `/w/${workspaceId}/configuracion/seguridad` : '/seguridad')}
        >
          Seguridad de la cuenta
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem icon={<Icon icon={Logout03Icon} />} onSelect={() => void signOut()}>
          Cerrar sesión
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SectionNav({ workspaceId, role, me, railKey, activeKey }: { workspaceId: string | null; role: Role | null; me: MeResponse; railKey: RailKey; activeKey: string | null }) {
  const [params] = useSearchParams();
  const projectId = params.get('proyecto');
  const items = moduleNav(railKey, role, me.user.capabilities);
  if (items.length === 0 || (!workspaceId && railKey !== 'platform')) return null;
  return (
    <NavGroup>
      {items.map((item) => (
        <NavItem key={item.key} label={item.label} icon={<Icon icon={item.icon} />} selected={activeKey === item.key} asChild>
          <Link to={navHref(item, workspaceId, item.capabilities ? null : projectId)} />
        </NavItem>
      ))}
    </NavGroup>
  );
}

export function WorkspaceRail({ workspaceId, role, me, railKey, modules }: { workspaceId: string | null; role: Role | null; me: MeResponse; railKey: RailKey; modules: ModuleEnablement | null }) {
  const [params] = useSearchParams();
  const projectId = params.get('proyecto');
  const items = visibleRail(workspaceId ? role : null, me.user.capabilities, modules);
  const render = (r: (typeof items)[number]) => (
    <ModuleRailItem
      key={r.module.key}
      label={r.module.shortLabel}
      title={r.module.label}
      icon={r.module.icon}
      selected={railKey === r.module.key}
      status={r.paused ? 'pausado' : undefined}
      asChild
    >
      <Link to={railHref(r.module, workspaceId, r.module.capabilities ? null : projectId)} />
    </ModuleRailItem>
  );
  const main = items.filter((r) => !r.module.capabilities);
  const platform = items.filter((r) => r.module.capabilities);
  return <ModuleRail footer={platform.length ? platform.map(render) : undefined}>{main.map(render)}</ModuleRail>;
}

/** Enabled state per module for the rail (null while loading or unavailable → defaults). */
function useModuleEnablement(workspaceId: string | null): ModuleEnablement | null {
  const q = useApiQuery(wk.modules(workspaceId ?? ''), 'listWorkspaceModules', { params: { workspaceId: workspaceId ?? '' } }, { enabled: !!workspaceId, staleTime: 60_000, retry: false });
  return React.useMemo(() => {
    if (!q.data) return null;
    const out = { whatsapp: true, ai_calls: true, copilot: false } as ModuleEnablement;
    for (const m of q.data.items) out[m.module] = m.enabled;
    return out;
  }, [q.data]);
}

const railLabel = (key: RailKey) => RAIL_MODULES.find((m) => m.key === key)?.label ?? '';

function NotificationsMenu({ workspaceId, home }: { workspaceId: string; home: Home | undefined }) {
  const navigate = useNavigate();
  const items = home?.attention ?? [];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <NotificationButton count={items.length} />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[min(360px,calc(100vw-32px))]">
        <DropdownMenuLabel>Requiere atención</DropdownMenuLabel>
        {home === undefined ? <p className="px-3 py-2 text-body text-text-secondary">Cargando…</p> : null}
        {home && items.length === 0 ? <p className="px-3 py-2 text-body text-text-secondary">Nada pendiente por ahora.</p> : null}
        {items.slice(0, 8).map((a, i) => (
          <DropdownMenuItem key={`${a.href}-${i}`} onSelect={() => navigate(a.href)} className="items-start">
            <span className="grid min-w-0 gap-0.5">
              <span className="truncate font-medium">{a.title}</span>
              {a.detail ? <span className="line-clamp-2 text-meta text-text-secondary">{a.detail}</span> : null}
              {a.at ? <span className="text-meta text-text-secondary">{formatRelative(a.at)}</span> : null}
            </span>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate(`/w/${workspaceId}/inicio`)}>Ver todo en Inicio</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function HeaderSearch({ workspaceId }: { workspaceId: string }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [q, setQ] = React.useState('');
  return (
    <form
      role="search"
      className="hidden w-64 xl:block"
      onSubmit={(e) => {
        e.preventDefault();
        const sp = new URLSearchParams();
        if (q.trim()) sp.set('q', q.trim());
        const p = params.get('proyecto');
        if (p) sp.set('proyecto', p);
        navigate(`/w/${workspaceId}/prospectos${sp.size ? `?${sp}` : ''}`);
      }}
    >
      <SearchInput value={q} onValueChange={setQ} label="Buscar prospectos" placeholder="Buscar prospectos…" />
    </form>
  );
}

/** Frame for workspace routes: sidebar, header (switcher, project filter, search, notifications), banners. */
/** `bare`: workspace context without the app frame (compact companion windows). */
export function WorkspaceLayout({ bare = false }: { bare?: boolean } = {}) {
  const { workspaceId = '' } = useParams();
  const me = useMe();
  const membership = me.data?.memberships.find((m) => m.workspaceId === workspaceId);
  const workspace = useApiQuery(wk.workspace(workspaceId), 'getWorkspace', { params: { workspaceId } }, { enabled: !!membership, staleTime: 60_000 });

  React.useEffect(() => {
    if (membership) rememberWorkspace(workspaceId);
  }, [membership, workspaceId]);

  if (me.isPending) return <FullPageLoading />;
  if (me.isError) return <FullPageLoading />;
  if (!membership) {
    return (
      <div className="grid min-h-dvh place-items-center bg-surface-canvas p-4">
        <EmptyState
          icon={<Icon icon={Building06Icon} />}
          title="No tienes acceso a este espacio de trabajo"
          description="Pide a un administrador del espacio que te invite. El acceso de soporte de Estateflow también requiere una membresía explícita."
          action={
            <Button asChild variant="secondary">
              <Link to="/">Ir a mis espacios</Link>
            </Button>
          }
        />
      </div>
    );
  }
  if (workspace.isPending) return <FullPageLoading label="Cargando espacio de trabajo" />;
  if (workspace.isError) {
    return (
      <div className="grid min-h-dvh place-items-center bg-surface-canvas p-4">
        <ErrorState
          description={workspace.error.message}
          reference={workspace.error instanceof ApiError ? workspace.error.code : undefined}
          onRetry={() => void workspace.refetch()}
          action={
            <Button asChild variant="secondary">
              <Link to="/">Ir a mis espacios</Link>
            </Button>
          }
        />
      </div>
    );
  }
  const role = membership.role;
  const ctx: WorkspaceContextValue = {
    workspaceId,
    workspace: workspace.data,
    membership,
    me: me.data,
    role,
    timezone: workspace.data.timezone || 'America/Mexico_City',
    can: makeCan(role),
    isAdmin: makeCan(role)('admin'),
  };
  return (
    <WorkspaceProvider value={ctx}>{bare ? <Outlet /> : <WorkspaceFrame ctx={ctx} />}</WorkspaceProvider>
  );
}

function ProjectFilter({ workspaceId }: { workspaceId: string }) {
  const projects = useApiQuery(wk.projects(workspaceId), 'listProjects', { params: { workspaceId } }, { staleTime: 60_000 });
  const [projectId, setProjectId] = useProjectFilter();
  if (!projects.data || projects.data.items.length === 0) return null;
  return (
    <ProjectPicker
      aria-label="Filtrar por proyecto"
      className="w-44 md:w-56"
      projects={projects.data.items.map((p) => ({ id: p.id, name: p.name, status: p.status }))}
      value={projectId}
      onValueChange={setProjectId}
    />
  );
}

function WorkspaceFrame({ ctx }: { ctx: WorkspaceContextValue }) {
  const { workspaceId, me, role, workspace, can } = ctx;
  const navigate = useNavigate();
  const location = useLocation();
  const home = useApiQuery(wk.home(workspaceId), 'home', { params: { workspaceId } }, { staleTime: 60_000, retry: false });
  const modules = useModuleEnablement(workspaceId);
  const railKey = railKeyFromPath(location.pathname) ?? 'general';
  const banner =
    workspace.isDemo || workspace.testMode ? (
      <>
        {workspace.isDemo ? <ModeBanner mode="demo" /> : null}
        {workspace.testMode ? <ModeBanner mode="test" /> : null}
      </>
    ) : undefined;
  return (
    // Advisor softphone (plan B3): mounted for members with role ≥ advisor; the widget floats over every page.
    <SoftphoneProvider workspaceId={workspaceId} enabled={can('advisor')}>
      <AppShell
        brand={<Brand />}
        banner={banner}
        rail={<WorkspaceRail workspaceId={workspaceId} role={role} me={me} railKey={railKey} modules={modules} />}
        navTitle={railLabel(railKey)}
        nav={<SectionNav workspaceId={workspaceId} role={role} me={me} railKey={railKey} activeKey={activeNavKey(location.pathname)} />}
        sidebarFooter={
          <div className="grid gap-1">
            <p className="px-2 text-meta text-text-secondary">
              Tu rol: <span className="font-medium text-text-primary">{ROLE_LABELS[role]}</span>
            </p>
            <UserMenu me={me} workspaceId={workspaceId} />
          </div>
        }
        header={
          <>
            <WorkspaceSwitcher
              workspaces={me.memberships.map((m) => ({
                id: m.workspaceId,
                name: m.workspaceName,
                role: m.role,
                setup: { completed: m.setupReady, total: m.setupTotal },
                attentionCount: m.attentionCount,
              }))}
              currentId={workspaceId}
              onSelect={(id) => navigate(`/w/${id}/inicio`)}
              onCreate={() => navigate('/espacios/nuevo')}
              className="max-w-40 sm:max-w-72"
            />
            <ProjectFilter workspaceId={workspaceId} />
            <div className="ml-auto flex items-center gap-2">
              {can('advisor') ? <HeaderSearch workspaceId={workspaceId} /> : null}
              <NotificationsMenu workspaceId={workspaceId} home={home.data} />
            </div>
          </>
        }
      >
        <React.Suspense fallback={null}>
          <Outlet />
        </React.Suspense>
      </AppShell>
      <SoftphoneWidget workspaceId={workspaceId} />
    </SoftphoneProvider>
  );
}

/** Frame for platform routes (not workspace-scoped). */
export function PlatformLayout() {
  const me = useMe();
  const navigate = useNavigate();
  if (!me.data) return <FullPageLoading />;
  const data = me.data;
  let lastId: string | null = null;
  try {
    lastId = sessionStorage.getItem('lia:last-workspace');
  } catch {
    lastId = null;
  }
  const current = data.memberships.find((m) => m.workspaceId === lastId) ?? data.memberships[0] ?? null;
  return (
    <AppShell
      brand={<Brand />}
      rail={<WorkspaceRail workspaceId={current?.workspaceId ?? null} role={current?.role ?? null} me={data} railKey="platform" modules={null} />}
      navTitle={railLabel('platform')}
      nav={<SectionNav workspaceId={current?.workspaceId ?? null} role={current?.role ?? null} me={data} railKey="platform" activeKey="plataforma" />}
      sidebarFooter={<UserMenu me={data} workspaceId={current?.workspaceId ?? null} />}
      header={
        data.memberships.length > 0 ? (
          <WorkspaceSwitcher
            workspaces={data.memberships.map((m) => ({
              id: m.workspaceId,
              name: m.workspaceName,
              role: m.role,
              setup: { completed: m.setupReady, total: m.setupTotal },
              attentionCount: m.attentionCount,
            }))}
            currentId={null}
            onSelect={(id) => navigate(`/w/${id}/inicio`)}
          />
        ) : (
          <span className="text-body text-text-secondary">Administración de plataforma</span>
        )
      }
    >
      <React.Suspense fallback={null}>
        <Outlet />
      </React.Suspense>
    </AppShell>
  );
}
